import React, { useState, useEffect, useCallback } from 'react';
import {
  Database, ArrowRight, RefreshCw, Loader2, AlertTriangle, CheckCircle,
  Play, Eye, FolderTree, AlertCircle, Percent
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Progress } from './ui/progress';

// API methods for migration - use api client methods
const migrationApi = {
  getStatus: () => api.getWBSMigrationStatus(),
  createLegacyBucket: () => api.getWBSLegacyBucket(),
  migrateToLegacy: (dryRun = true, collection = null) => 
    api.migrateToLegacyBucket(dryRun, collection),
  validateOrphans: () => api.validateWBSOrphans(),
  repairOrphans: (dryRun = true) => 
    api.repairWBSOrphans(dryRun),
};

// Collection display names
const COLLECTION_NAMES = {
  epics: 'Epics',
  features: 'Features',
  work_items: 'Work Items',
  requirements: 'Requirements',
  issues: 'Issues',
  decisions: 'Decisions',
};

const WBSMigrationPanel = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [legacyBucket, setLegacyBucket] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);

  // Load migration status
  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, bucketRes] = await Promise.all([
        migrationApi.getStatus(),
        migrationApi.createLegacyBucket().catch(() => null),
      ]);
      setStatus(statusRes.data?.data || statusRes.data);
      if (bucketRes?.data?.data) {
        setLegacyBucket(bucketRes.data.data);
      } else if (bucketRes?.data) {
        setLegacyBucket(bucketRes.data);
      }
    } catch (err) {
      console.error('Failed to load migration status:', err);
      toast.error('Failed to load migration status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Preview migration
  const handlePreview = async () => {
    setMigrating(true);
    try {
      const res = await migrationApi.migrateToLegacy(true);
      setPreviewResult(res.data?.data || res.data);
      setShowMigrationDialog(true);
    } catch (err) {
      toast.error('Failed to preview migration');
    } finally {
      setMigrating(false);
    }
  };

  // Execute migration
  const handleMigrate = async () => {
    setMigrating(true);
    try {
      const res = await migrationApi.migrateToLegacy(false);
      const result = res.data?.data || res.data;
      toast.success(`Migration complete: ${result.total_migrated} items migrated`);
      setShowMigrationDialog(false);
      loadStatus();
    } catch (err) {
      toast.error('Migration failed');
    } finally {
      setMigrating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const totalItems = status?.totals?.total_items || 0;
  const itemsWithWbs = status?.totals?.items_with_wbs || 0;
  const itemsWithoutWbs = status?.totals?.items_without_wbs || 0;
  const percentage = status?.totals?.migration_percentage || 0;

  return (
    <div className="bg-white rounded-xl border p-6" data-testid="wbs-migration-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Database className="w-5 h-5 text-purple-600" />
            WBS Migration Status
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Track and execute migration of existing items to WBS structure
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadStatus}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Progress Overview */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Migration Progress</span>
          <span className="text-sm font-semibold text-purple-600">{percentage}%</span>
        </div>
        <Progress value={percentage} className="h-2" />
        <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
          <span>{itemsWithWbs} items linked to WBS</span>
          <span>{itemsWithoutWbs} items pending</span>
        </div>
      </div>

      {/* Legacy Bucket Info */}
      {legacyBucket && (
        <div className="mb-6 p-4 border rounded-lg bg-amber-50 border-amber-200">
          <div className="flex items-center gap-2 mb-2">
            <FolderTree className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">Legacy Bucket</span>
          </div>
          <p className="text-xs text-amber-700">
            <strong>{legacyBucket.name}</strong> (Code: {legacyBucket.code})
          </p>
          <p className="text-xs text-amber-600 mt-1">
            Items without a proper WBS node will be assigned here during migration.
          </p>
        </div>
      )}

      {/* Collection Breakdown */}
      <div className="space-y-3 mb-6">
        <h4 className="text-sm font-medium text-gray-700">By Collection</h4>
        {status?.collections && Object.entries(status.collections).map(([key, data]) => (
          <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-800">
                {COLLECTION_NAMES[key] || key}
              </span>
              <span className="text-xs text-gray-500">
                {data.total} total
              </span>
            </div>
            <div className="flex items-center gap-3">
              {data.without_wbs > 0 ? (
                <span className="flex items-center gap-1 text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded">
                  <AlertTriangle className="w-3 h-3" />
                  {data.without_wbs} pending
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                  <CheckCircle className="w-3 h-3" />
                  All linked
                </span>
              )}
              <div className="w-16">
                <Progress value={data.percentage} className="h-1.5" />
              </div>
              <span className="text-xs text-gray-500 w-10 text-right">
                {data.percentage}%
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        {itemsWithoutWbs > 0 ? (
          <>
            <Button 
              variant="outline" 
              onClick={handlePreview}
              disabled={migrating}
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview Migration
            </Button>
            <Button 
              onClick={handlePreview}
              disabled={migrating}
              data-testid="start-migration-btn"
            >
              {migrating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Migrate to Legacy Bucket
            </Button>
          </>
        ) : (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm font-medium">All items are linked to WBS nodes</span>
          </div>
        )}
      </div>

      {/* Migration Confirmation Dialog */}
      <Dialog open={showMigrationDialog} onOpenChange={setShowMigrationDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-purple-600" />
              Confirm WBS Migration
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <p className="text-sm text-gray-600">
              This will assign all items without a WBS node to the <strong>"Legacy / Unstructured Work"</strong> bucket.
            </p>

            {previewResult && (
              <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700">Items to be migrated:</h4>
                {Object.entries(previewResult.collections).map(([key, data]) => (
                  data.items_to_migrate > 0 && (
                    <div key={key} className="flex justify-between text-sm">
                      <span className="text-gray-600">{COLLECTION_NAMES[key] || key}</span>
                      <span className="font-medium">{data.items_to_migrate} items</span>
                    </div>
                  )
                ))}
                <div className="border-t pt-2 mt-2 flex justify-between text-sm font-semibold">
                  <span>Total</span>
                  <span className="text-purple-600">
                    {Object.values(previewResult.collections).reduce((sum, d) => sum + d.items_to_migrate, 0)} items
                  </span>
                </div>
              </div>
            )}

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                <p className="text-xs text-amber-700">
                  After migration, you can reassign items to their proper WBS nodes using the bulk assignment tool or by editing individual items.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMigrationDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleMigrate} disabled={migrating}>
              {migrating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Execute Migration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WBSMigrationPanel;
